import { useState } from 'react';
import type { Review } from '@/mocks/movies';
import RatingStars from '@/components/base/RatingStars';

interface ReviewListProps {
  reviews: Review[];
}

function ReviewCard({ review, index }: { review: Review; index: number }) {
  const [expanded, setExpanded] = useState(index === 0);
  const isLong = review.content.length > 90;

  return (
    <article className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold shrink-0">
            {review.author.replace(/^.*·/, '').charAt(0)}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 truncate">{review.author}</p>
            <p className="text-xs text-foreground-400">{review.role} · {review.date}</p>
          </div>
        </div>
        <RatingStars rating={review.rating} size="md" />
      </div>

      <h3 className="mt-4 text-base md:text-lg font-semibold text-foreground-950">
        {review.title}
      </h3>

      <p
        className={`mt-2 text-sm leading-relaxed text-foreground-600 ${
          !expanded && isLong ? 'line-clamp-3' : ''
        }`}
      >
        {review.content}
      </p>

      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
        >
          {expanded ? '收起全文' : '展开全文'}
          <i
            className={`${
              expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            } w-4 h-4 flex items-center justify-center`}
          ></i>
        </button>
      )}
    </article>
  );
}

export default function ReviewList({ reviews }: ReviewListProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
      {reviews.map((r, i) => (
        <ReviewCard key={r.id} review={r} index={i} />
      ))}
    </div>
  );
}